import { Users, CalendarDays, TrendingUp, MessageCircle, UserCheck, MapPin } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const Stat = ({ icon: Icon, label, value, hint, testId }) => (
  <Card className="bg-obsidian-200 border-white/10">
    <CardContent className="p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-kairos-orange/15 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-kairos-orange" /></div>
      <div>
        <p className="text-gray-500 text-xs">{label}</p>
        <p className="text-white text-2xl font-bold leading-tight" data-testid={testId}>{value}</p>
        {hint && <p className="text-gray-500 text-xs">{hint}</p>}
      </div>
    </CardContent>
  </Card>
);

const Bars = ({ data, testId }) => {
  const max = Math.max(1, ...data.map((d) => d.total));
  return (
    <div className="flex items-end gap-[3px] h-24" data-testid={testId} aria-label="Evolução diária dos cadastros">
      {data.map((d) => (
        <div key={d.dia} className="flex-1 h-full flex flex-col justify-end group relative" title={`${d.dia.split('-').reverse().join('/')}: ${d.total}`}>
          <div className={`rounded-t ${d.total ? 'bg-kairos-orange' : 'bg-white/10'} transition-all`} style={{ height: `${Math.max(4, (d.total / max) * 100)}%` }} />
        </div>
      ))}
    </div>
  );
};

const List = ({ title, obj, testId }) => (
  <Card className="bg-obsidian-200 border-white/10">
    <CardContent className="p-5">
      <p className="text-gray-400 text-sm mb-3">{title}</p>
      <ul className="space-y-2" data-testid={testId}>
        {Object.entries(obj || {}).slice(0, 6).map(([k, v]) => (
          <li key={k} className="flex items-center justify-between text-sm"><span className="text-gray-300 truncate pr-3">{k}</span><span className="text-white font-semibold">{v}</span></li>
        ))}
        {!Object.keys(obj || {}).length && <li className="text-gray-600 text-sm">Sem dados ainda</li>}
      </ul>
    </CardContent>
  </Card>
);

export const PreInscricoesStats = ({ stats }) => {
  if (!stats) return null;
  return (
    <div className="space-y-4" data-testid="pre-inscricoes-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total de casais" value={stats.total} testId="stat-total" />
        <Stat icon={CalendarDays} label="Hoje" value={stats.hoje} hint={`${stats.ultimos_7_dias} nos últimos 7 dias`} testId="stat-hoje" />
        <Stat icon={TrendingUp} label="Últimos 30 dias" value={stats.ultimos_30_dias} testId="stat-30d" />
        <Stat icon={MessageCircle} label="Aceitam comunicações" value={stats.com_consentimento_comunicacoes} testId="stat-consent" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-obsidian-200 border-white/10 lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Evolução diária (30 dias)</p>
              <p className="text-xs text-gray-500 flex items-center gap-3"><span className="flex items-center gap-1"><UserCheck className="w-3 h-3 text-kairos-orange" /> {stats.ja_participaram} já participaram</span><span>{stats.novos} novos</span></p>
            </div>
            <Bars data={stats.evolucao_diaria || []} testId="stat-evolucao" />
          </CardContent>
        </Card>
        <List title="Origem das pré-inscrições" obj={stats.por_origem} testId="stat-origem" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <List title="Estados (UF)" obj={stats.por_estado} testId="stat-estados" />
        <List title="Cidades" obj={stats.por_cidade} testId="stat-cidades" />
      </div>
    </div>
  );
};

export const STATUS_LABELS = {
  novo: { label: 'Novo', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  contatado: { label: 'Contatado', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  aguardando_lote: { label: 'Aguardando abertura do lote', cls: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  inscricao_iniciada: { label: 'Inscrição iniciada', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  inscricao_confirmada: { label: 'Inscrição confirmada (paga)', cls: 'bg-green-500/15 text-green-300 border-green-500/30' },
  desistencia: { label: 'Desistência', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

export const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || STATUS_LABELS.novo;
  return <span className={`px-2.5 py-1 rounded-full text-xs border whitespace-nowrap ${s.cls}`}>{s.label}</span>;
};

export const CidadeUf = ({ item }) => (
  <span className="inline-flex items-center gap-1 text-gray-400 text-sm"><MapPin className="w-3 h-3" />{item.cidade}/{item.estado}</span>
);
